import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import withPermission from "../../../core/common/autorisations/withPermission";
import PaymentRecuModalApercu from "./PaymentRecuModalApercu";

type Props = {
  show: boolean;
  payment?: any;
  currency?: string;
  onClose: () => void;
};

// boutton protégée pour imprimer un détail
const ProtectedBtnPrintDetails = withPermission(
  (props: any) => (
    <div className="mb-2">
      <button
        onClick={() => {
          props.setShowPaymentRecu(true);
        }}
        className="btn btn-primary d-flex align-items-center"
      >
        <i className="ti ti-printer me-2" />
        Imprimer
      </button>
    </div>
  ),
  [buildPermission(Resource.PAYMENT, Action.CREATE)],
  "masquer",
);

const PaymentDetailsModal = ({
  show,
  payment,
  onClose,
  currency = "FCFA",
}: Props) => {
  const repartitions = useMemo(
    () => (Array.isArray(payment?.repartitions) ? payment.repartitions : []),
    [payment],
  );

  const [showPaymentRecu, setShowPaymentRecu] = useState<boolean>(false);

  const allocatedTotal = useMemo(
    () =>
      repartitions.reduce(
        (sum: number, repartition: any) =>
          sum + Number(repartition?.amount || 0),
        0,
      ),
    [repartitions],
  );

  const totalAmount = Number(
    payment?.amount ??
      payment?.amount_total ??
      payment?.amountReceived ??
      allocatedTotal,
  );

  const paymentId = payment?.payment_id || payment?.id || "-";

  return (
    <>
      <Modal show={show} onHide={onClose} centered size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title className="d-flex justify-content-between w-100">
            Détails du paiement{" "}
            <ProtectedBtnPrintDetails setShowPaymentRecu={setShowPaymentRecu} />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-white">
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <div className="border border-2 border-secondary-subtle rounded-3 p-3 h-100">
                <h6 className="mb-2">Informations générales</h6>
                <div className="fw-semibold text-dark small text-muted mb-1">
                  N° Recu
                </div>
                <div className="fw-semibold mb-2">
                  {payment?.number_receipt ? payment?.number_receipt : "-"}
                </div>
                <div className="fw-semibold text-dark small text-muted mb-1">
                  Date de réception
                </div>
                <div className="mb-2">
                  {payment?.reception_date
                    ? new Date(payment.reception_date).toLocaleString("fr-FR")
                    : "-"}
                </div>
                <div className="fw-semibold text-dark small text-muted mb-1">
                  Type
                </div>
                <div className="mb-2">{payment?.type || "-"}</div>
                <div className="fw-semibold text-dark small text-muted mb-1">
                  Description
                </div>
                <div className="mb-2">{payment?.description || "-"}</div>
                <div className="fw-semibold text-dark small text-muted mb-1">
                  Enregistrer par
                </div>
                <div className="mb-2">
                  {`${payment?.historiquePayment?.lastname} ${payment?.historiquePayment?.firstname}` ||
                    "-"}
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="border border-2 border-secondary-subtle rounded-3 p-3 h-100">
                <h6 className="mb-2">Montants & banque</h6>
                <div className="fw-semibold text-dark small text-muted mb-1">
                  Montant total
                </div>
                <div className="fw-semibold text-primary mb-2">
                  {totalAmount.toLocaleString("fr-FR")} {currency}
                </div>
                <div className="fw-semibold text-dark small text-muted mb-1">
                  Compte bancaire
                </div>
                <div className="mb-1">
                  {payment?.bank_account?.bank_name || "-"} -{" "}
                  {payment?.bank_account?.account_title || "-"}
                </div>
                <div className="mb-1">
                  N°: {payment?.bank_account?.account_number || "-"}
                </div>
                <div>Type: {payment?.bank_account?.type || "-"}</div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm border-dark rounded">
            <div className="card-header bg-light">
              <h6 className="mb-0">Répartitions</h6>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table mb-0">
                  <thead>
                    <tr>
                      <th>Plan</th>
                      <th>Échéance</th>
                      <th>Montant du plan</th>
                      <th>Montant alloué</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!repartitions.length && (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-3">
                          Aucune répartition disponible
                        </td>
                      </tr>
                    )}
                    {repartitions.map((repartition: any) => (
                      <tr key={repartition?.repartition_id || repartition?.id}>
                        <td>{repartition?.payment_plan?.label || "-"}</td>
                        <td>
                          {repartition?.payment_plan?.due_date
                            ? new Date(
                                repartition.payment_plan.due_date,
                              ).toLocaleDateString("fr-FR")
                            : "-"}
                        </td>
                        <td>
                          {Number(
                            repartition?.payment_plan?.amount || 0,
                          ).toLocaleString("fr-FR")}{" "}
                          {currency}
                        </td>
                        <td className="fw-semibold">
                          {Number(repartition?.amount || 0).toLocaleString(
                            "fr-FR",
                          )}{" "}
                          {currency}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="fw-semibold">Total alloué</td>
                      <td></td>
                      <td></td>
                      <td className="fw-semibold text-primary">
                        {allocatedTotal.toLocaleString("fr-FR")} {currency}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      <PaymentRecuModalApercu
        show={showPaymentRecu}
        onClose={() => setShowPaymentRecu(false)}
        metaData={{ id: paymentId }}
      />
    </>
  );
};

export default withPermission(
  PaymentDetailsModal,
  [
    buildPermission(Resource.PAYMENT, Action.READ),
    buildPermission(Resource.PAYMENT, Action.CREATE),
    buildPermission(Resource.PAYMENT, Action.UPDATE),
    buildPermission(Resource.PAYMENT, Action.DELETE),
  ],
  "masquer",
);
